import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingBag,
  Save,
  Info,
  DollarSign,
  Percent,
  Hash,
  Shirt,
  Search,
  Plus,
  GripVertical,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SETTINGS = {
  print_prices: { xs: 15, s: 17, m: 20, large: 25, xl: 30 },
  quantity_discounts: { qty_2_3: 10, qty_4_6: 20, qty_7_10: 25 },
  additional_print_discount: { value: 5 },
  byo_pricing: { one_print: 35, two_prints: 50, three_prints: 65, additional_print: 15 },
  rounding: { mode: "none" },
  retail_presets: [],
};

const ROUNDING_OPTIONS = [
  { value: "none", label: "No rounding" },
  { value: "nearest_1", label: "Round to nearest $1" },
  { value: "up_1", label: "Round up to nearest $1" },
  { value: "nearest_5", label: "Round to nearest $5" },
];

export default function RetailPricingContent() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getToken = () => localStorage.getItem("auth_token");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/settings/retail-pricing`, { headers });
      if (res.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...res.data });
      }
    } catch (error) {
      console.error("Failed to load retail pricing", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/settings/retail-pricing`, settings, { headers });
      toast.success("Retail pricing saved");
    } catch (error) {
      console.error("Failed to save", error);
      toast.error("Failed to save retail pricing");
    } finally {
      setIsSaving(false);
    }
  };

  const updatePrintPrice = (size, value) => {
    setSettings(prev => ({
      ...prev,
      print_prices: { ...prev.print_prices, [size]: parseFloat(value) || 0 },
    }));
  };

  const updateQuantityDiscount = (tier, value) => {
    setSettings(prev => ({
      ...prev,
      quantity_discounts: { ...prev.quantity_discounts, [tier]: parseFloat(value) || 0 },
    }));
  };

  const updateBYO = (field, value) => {
    setSettings(prev => ({
      ...prev,
      byo_pricing: { ...prev.byo_pricing, [field]: parseFloat(value) || 0 },
    }));
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center py-12 text-gray-400">
        <div className="animate-pulse">Loading retail pricing...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-orange-600" />
            Retail Pricing
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure retail pricing rules and markups (all prices include GST)
          </p>
        </div>
        <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-9 text-xs">
          <Save className="w-3 h-3 mr-1.5" />
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Print Prices by Size */}
        <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-400" />
            Print Prices by Size (inc. GST)
          </h3>
          <div className="grid grid-cols-5 gap-3">
            {[
              { key: "xs", label: "XS" },
              { key: "s", label: "S" },
              { key: "m", label: "M" },
              { key: "large", label: "L" },
              { key: "xl", label: "XL" },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs text-gray-500">{label}</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={settings.print_prices?.[key] || ""}
                    onChange={(e) => updatePrintPrice(key, e.target.value)}
                    className="pl-6 h-9 text-sm font-mono border-gray-200"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quantity Discounts */}
        <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Percent className="w-4 h-4 text-gray-400" />
            Quantity Discounts
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "qty_2_3", label: "2-3 garments" },
              { key: "qty_4_6", label: "4-6 garments" },
              { key: "qty_7_10", label: "7-10 garments" },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs text-gray-500">{label}</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="1"
                    value={settings.quantity_discounts?.[key] || ""}
                    onChange={(e) => updateQuantityDiscount(key, e.target.value)}
                    className="pr-6 h-9 text-sm font-mono border-gray-200"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* BYO Pricing */}
        <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Shirt className="w-4 h-4 text-gray-400" />
            BYO Garment Pricing (inc. GST)
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {[
              { key: "one_print", label: "1 Print" },
              { key: "two_prints", label: "2 Prints" },
              { key: "three_prints", label: "3 Prints" },
              { key: "additional_print", label: "Additional" },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs text-gray-500">{label}</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={settings.byo_pricing?.[key] || ""}
                    onChange={(e) => updateBYO(key, e.target.value)}
                    className="pl-6 h-9 text-sm font-mono border-gray-200"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rounding */}
        <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Hash className="w-4 h-4 text-gray-400" />
            Price Rounding
          </h3>
          <Select
            value={settings.rounding?.mode || "none"}
            onValueChange={(value) => setSettings(prev => ({
              ...prev,
              rounding: { ...prev.rounding, mode: value },
            }))}
          >
            <SelectTrigger className="w-48 h-9 text-sm border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROUNDING_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
